import type {
	GraphEdge,
	GraphNode,
	GraphPosition,
	GraphPositions,
} from './types';

export const NODE_WIDTH = 190;
export const NODE_HEIGHT = 64;

export function boundPosition(position: GraphPosition): GraphPosition {
	return {
		x: Math.max(-9500, Math.min(9500, position.x)),
		y: Math.max(-9500, Math.min(9500, position.y)),
	};
}

function directedLayers(
	members: GraphNode[],
	connections: GraphEdge[],
): Map<string, number> {
	const targets = new Set(connections.map((edge) => edge.target));
	const layers = new Map<string, number>();
	const queue = members
		.filter((node) => !targets.has(node.id))
		.map((node) => node.id);
	for (const root of queue) {
		layers.set(root, 0);
	}
	for (const node of members) {
		if (!layers.has(node.id) && !queue.length) {
			queue.push(node.id);
			layers.set(node.id, 0);
		}
		while (queue.length) {
			const id = queue.shift();
			if (!id) {
				break;
			}
			const children = connections.filter(
				(edge) => edge.source === id && !layers.has(edge.target),
			);
			for (const edge of children) {
				layers.set(edge.target, Math.min(7, (layers.get(id) ?? 0) + 1));
				queue.push(edge.target);
			}
		}
	}
	return layers;
}

/** Directed breadth-first layers, with deterministic cycle roots and separate scope bands. */
export function graphPositions(
	nodes: GraphNode[],
	saved: GraphPositions = {},
	edges: GraphEdge[] = [],
): GraphPositions {
	const result: GraphPositions = {};
	const scopes = new Map<string, GraphNode[]>();
	for (const node of [...nodes].sort((a, b) => a.id.localeCompare(b.id))) {
		const scope = scopes.get(node.scope) ?? [];
		scope.push(node);
		scopes.set(node.scope, scope);
	}
	let bandY = 80;
	for (const [scope, members] of scopes) {
		const connections = edges.filter(
			(edge) => edge.scope === scope && edge.source !== edge.target,
		);
		const layers = directedLayers(members, connections);
		const rows = new Map<number, number>();
		for (const node of members) {
			const column = layers.get(node.id) ?? 0;
			const row = rows.get(column) ?? 0;
			rows.set(column, row + 1);
			const stored = saved[node.id];
			if (stored && Number.isFinite(stored.x) && Number.isFinite(stored.y)) {
				result[node.id] = boundPosition(stored);
				continue;
			}
			result[node.id] = {
				x: 80 + column * 290,
				y: bandY + row * 104,
			};
		}
		bandY += Math.max(...rows.values(), 1) * 104 + 80;
	}
	return result;
}

export function graphBounds(positions: GraphPositions): {
	x: number;
	y: number;
	width: number;
	height: number;
} {
	const points = Object.values(positions);
	if (!points.length) {
		return { x: 0, y: 0, width: 800, height: 400 };
	}
	const x = Math.min(...points.map((p) => p.x)) - 100;
	const y = Math.min(...points.map((p) => p.y)) - 100;
	return {
		x,
		y,
		width: Math.max(
			400,
			Math.max(...points.map((p) => p.x)) + NODE_WIDTH + 100 - x,
		),
		height: Math.max(
			240,
			Math.max(...points.map((p) => p.y)) + NODE_HEIGHT + 100 - y,
		),
	};
}

export function edgePath(edge: GraphEdge, positions: GraphPositions): string {
	const source = positions[edge.source];
	const target = positions[edge.target];
	if (edge.source === edge.target) {
		return `M${source.x + NODE_WIDTH - 20},${source.y} C${source.x + 280},${source.y - 90} ${source.x + 280},${source.y + 110} ${source.x + NODE_WIDTH},${source.y + 40}`;
	}
	const forward = target.x >= source.x + NODE_WIDTH;
	const sx = source.x + NODE_WIDTH;
	const sy = source.y + NODE_HEIGHT / 2;
	const tx = target.x;
	const ty = target.y + NODE_HEIGHT / 2;
	if (forward) {
		const mid = (sx + tx) / 2;
		return `M${sx},${sy} C${mid},${sy} ${mid},${ty} ${tx},${ty}`;
	}
	return `M${sx},${sy} C${sx + 80},${sy} ${tx - 80},${ty} ${tx},${ty}`;
}
